import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";

import useArrayEffect from "@kikoff/hooks/src/useArrayEffect";
import usePrevious from "@kikoff/hooks/src/usePrevious";
import { combineClasses } from "@kikoff/utils/src/string";

import styles from "./grid_list.module.scss";

type ColumnListProps = {
  minColumns?: number;
  delay?: number;
  show?: boolean;
  className?: string;
  propsFor?: {
    col?: React.HTMLProps<HTMLDivElement>;
    item?: React.HTMLProps<HTMLDivElement>;
  };
} & (
  | // Column width unecessary for grid list with single column
  {
      maxColumns: 1;
      columnWidth?: number;
    }
  | {
      maxColumns?: number;
      columnWidth: number;
    }
);

const ColumnList: React.FC<ColumnListProps> = ({
  children: _children,
  minColumns = 1,
  maxColumns,
  columnWidth,
  delay = 50,
  show = true,
  className,
  propsFor,
  ...props
}) => {
  const children = React.Children.toArray(_children) as JSX.Element[];
  const prevChildren = usePrevious(children);

  const [childIndex, setChildIndex] = useState(0);
  const [cols, setCols] = useState<number[][]>([]);
  const ref = useRef<HTMLDivElement>(null!);

  const getColsLen = (clientWidth: number) => {
    if (maxColumns === 1) return 1;

    const colCount = Math.floor(clientWidth / (columnWidth as number));

    return Math.max(
      maxColumns ? Math.min(colCount, maxColumns) : colCount,
      minColumns
    );
  };

  useEffect(() => {
    const root = ref.current;
    setCols([...Array(getColsLen(root.clientWidth))].map(() => []));

    const observer = new window.ResizeObserver(() => {
      if (
        // Compensating for scrollbar, if the list eventually creates a scrollbar,
        // then the scrollbar may force the ColumnList to have a different number of
        // columns if the clientWidth is within a certain range, which may cause the
        // scrollbar to go away because it will regenerate with 0 height. This will
        // indefinitely loop without this schmitt-trigger-like compensation.
        ![
          getColsLen(root.clientWidth),
          getColsLen(root.clientWidth - 20),
        ].includes(root.getElementsByClassName(styles.col).length)
      ) {
        setChildIndex(-1);
      }
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (childIndex === -1) {
      setCols([...Array(getColsLen(ref.current.clientWidth))].map(() => []));

      setChildIndex(0);
    } else if (show) {
      setCols((_cols) => {
        const colElements = ref.current.getElementsByClassName(styles.col);
        const col = Array.from(colElements)
          .slice(1)
          .reduce(
            (acc, curr, i) => {
              const height = curr.scrollHeight;
              return height < acc.minHeight
                ? { minHeight: height, index: i + 1 }
                : acc;
            },
            {
              minHeight: colElements[0]?.scrollHeight,
              index: 0,
            }
          ).index;
        _cols[col].push(childIndex);
        return _cols;
      });

      if (childIndex < children.length) {
        if (delay) {
          const timeout = setTimeout(
            () => setChildIndex(childIndex + 1),
            delay
          );
          return () => {
            clearTimeout(timeout);
          };
        }
        setChildIndex(childIndex + 1);
      }
    }
  }, [childIndex, show]);

  // Regenerate if child array changes
  const childrenKeys = children.map(({ key }) => key);
  useArrayEffect(() => {
    setChildIndex(-1);
  }, childrenKeys);

  useEffect(() => {
    if (childIndex > 0 && !show) {
      gsap
        .timeline()
        .to(ref.current.getElementsByClassName(styles.item), {
          duration: 0.5,
          height: 0,
          opacity: 0,
        })
        .call(() => setChildIndex(-1));
    }
  }, [show]);

  return (
    <div
      className={combineClasses(styles["grid-list"], className)}
      ref={ref}
      {...props}
    >
      {cols.map((col, i) => {
        return (
          <div
            {...propsFor?.col}
            className={combineClasses(styles.col, propsFor?.col?.className)}
            key={i}
          >
            {[...new Set(col)].map((_childIndex, j) => {
              return (
                <div
                  {...propsFor?.item}
                  className={combineClasses(
                    styles.item,
                    propsFor?.item?.className
                  )}
                  key={j}
                  onAnimationEnd={children[_childIndex]?.props?.onGridAppear}
                >
                  {children[_childIndex]}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
export default ColumnList;
